"use client";

import { useEffect, useRef, useState } from "react";

type LogoShape = "unknown" | "mark" | "wordmark" | "failed";

/**
 * The operator's logo next to their name. A wide wordmark (2.2:1 or wider) already spells the name, so the name is not
 * repeated; a logo that fails to load leaves the name alone. Same rule as the shared public-rental brand mark.
 * In the header the logo sits on a tile that turns light over the hero photo, so any logo stays legible on any photo.
 */
export function BrandMark({ name, logoUrl, tile = false }: { name: string; logoUrl: string | null; tile?: boolean }) {
  const [shape, setShape] = useState<LogoShape>("unknown");
  const image = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const element = image.current;
    if (!element || !element.complete) return;
    // Loaded before hydration: the load event already fired, so read the size now.
    const ratio = element.naturalHeight > 0 ? element.naturalWidth / element.naturalHeight : 0;
    const next: LogoShape = element.naturalWidth === 0 ? "failed" : ratio >= 2.2 ? "wordmark" : "mark";
    const frame = requestAnimationFrame(() => setShape(next));
    return () => cancelAnimationFrame(frame);
  }, []);

  const classify = (element: HTMLImageElement) => {
    const ratio = element.naturalHeight > 0 ? element.naturalWidth / element.naturalHeight : 0;
    setShape(ratio >= 2.2 ? "wordmark" : "mark");
  };

  const showLogo = Boolean(logoUrl) && shape !== "failed";
  const logo = showLogo ? (
    <img
      ref={image}
      className="hf-logo"
      src={logoUrl ?? undefined}
      alt={shape === "wordmark" ? name : ""}
      onLoad={(event) => classify(event.currentTarget)}
      onError={() => setShape("failed")}
    />
  ) : null;
  return (
    <span className="hf-brand-in" data-wordmark={shape === "wordmark" ? "" : undefined}>
      {logo && tile ? <span className="hf-logo-tile">{logo}</span> : logo}
      {shape === "wordmark" ? null : <span className="hf-brand-name">{name}</span>}
    </span>
  );
}
